import {
	Box,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material'

import { useAppContext } from '@/hooks'
import { APPROVER_COUNT_MAX } from '@/utils/approverMinimum'

import { BranchSourceOption } from '../hooks/useCreateBranchDialog'

type CreateBranchFormProps = {
	approvalsValid: boolean
	description: string
	minimumNumberOfApprovers: number
	name: string
	onDescriptionChange: (value: string) => void
	onNameChange: (value: string) => void
	onRequiredApprovalsChange: (value: number) => void
	onSourceIdChange: (value: string) => void
	requiredApprovals: number
	sourceId: string
	sourceOptions: BranchSourceOption[]
}

export const CreateBranchForm = ({
	approvalsValid,
	description,
	minimumNumberOfApprovers,
	name,
	onDescriptionChange,
	onNameChange,
	onRequiredApprovalsChange,
	onSourceIdChange,
	requiredApprovals,
	sourceId,
	sourceOptions,
}: CreateBranchFormProps) => {
	const { t } = useAppContext()
	const rangeText = t('BRANCHES_REQUIRED_APPROVALS_RANGE', [
		minimumNumberOfApprovers,
		APPROVER_COUNT_MAX,
	])

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
				gap: 2,
			}}
		>
			<Box sx={{ gridColumn: '1 / -1' }}>
				<InputLabel
					sx={{ mb: 0.75, fontSize: 12, fontWeight: 600, color: '#52666C' }}
				>
					{t('BRANCHES_SOURCE_BRANCH')}
				</InputLabel>
				<Select
					fullWidth
					size="small"
					value={sourceId}
					onChange={(event) => onSourceIdChange(event.target.value)}
					displayEmpty
					data-testid="select-input-branch-source"
					sx={{ borderRadius: '9px', fontSize: 13 }}
				>
					<MenuItem value="" disabled>
						{t('BRANCHES_SELECT_SOURCE')}
					</MenuItem>
					{sourceOptions.map((source) => (
						<MenuItem key={source.id} value={source.id} sx={{ fontSize: 13 }}>
							{source.name}
						</MenuItem>
					))}
				</Select>
			</Box>
			<TextField
				label={t('NAME')}
				value={name}
				onChange={(event) => onNameChange(event.target.value)}
				placeholder={t('BRANCHES_NAME_PLACEHOLDER')}
				size="small"
				fullWidth
				slotProps={{
					htmlInput: { 'data-testid': 'text-input-branch-name' },
					input: { sx: { borderRadius: '9px', fontSize: 13 } },
				}}
			/>
			<TextField
				label={t('BRANCHES_REQUIRED_APPROVALS')}
				helperText={`${t('BRANCHES_REQUIRED_APPROVALS_HINT')} ${rangeText}`}
				error={!approvalsValid}
				value={Number.isNaN(requiredApprovals) ? '' : requiredApprovals}
				onChange={(event) => {
					const raw = event.target.value
					if (raw === '') {
						onRequiredApprovalsChange(Number.NaN)
						return
					}
					const next = Number(raw)
					onRequiredApprovalsChange(
						Number.isInteger(next) ? next : Number.NaN,
					)
				}}
				type="number"
				size="small"
				fullWidth
				slotProps={{
					htmlInput: {
						min: minimumNumberOfApprovers,
						max: APPROVER_COUNT_MAX,
						step: 1,
						'data-testid': 'text-input-branch-required-approvals',
					},
					input: { sx: { borderRadius: '9px', fontSize: 13 } },
				}}
			/>
			<TextField
				label={t('DESCRIPTION')}
				value={description}
				onChange={(event) => onDescriptionChange(event.target.value)}
				size="small"
				fullWidth
				multiline
				minRows={3}
				slotProps={{
					htmlInput: { 'data-testid': 'text-input-branch-description' },
					input: { sx: { borderRadius: '9px', fontSize: 13 } },
				}}
				sx={{ gridColumn: '1 / -1' }}
			/>
		</Box>
	)
}
